import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import type { CreateProjectDraft } from '../../ui/create-project-form.component';
import { libraryOffer } from '../../ui/template-picker-cards';
import { PartnerShopResource } from '../../../designs/domain/infrastructure/partner-shop.resource';
import { LumirLibraryResource } from '../../../designs/domain/infrastructure/partner-showcase.resource';
import { ProjectsApiService } from '../infrastructure/projects-api.service';
import { ProjectsFacade } from './projects.facade';
import { buildPhotoBookSettingsPatch } from './default-print-product.service';
import { CreateProjectPreferencesService } from './create-project-preferences.service';
import { PhotoUploadCoordinatorService } from './photo-upload-coordinator.service';
import { birthLibraryPickOf, rememberBirthLibraryPick, rememberBirthRequest } from './project-birth-mark';

/** Second refusal of a free library grant: the project still works, the pack just is not in «Мои шаблоны». */
export const LIBRARY_PICK_FAILED = 'Шаблон не добавился в «Мои шаблоны»';

/** Принятый черновик «Нового проекта» → мастер: общий путь шага настроек и мобильной шторки. */
@Injectable({ providedIn: 'root' })
export class ProjectCreationService {
  private readonly router = inject(Router);
  private readonly projects = inject(ProjectsFacade);
  private readonly createProjectPreferences = inject(CreateProjectPreferencesService);
  private readonly toast = inject(ToastService);
  // Resolved at claim time only: both resources open their reads on injection, and the shell builds this service on every visit.
  private readonly injector = inject(Injector);
  private readonly projectsApi = inject(ProjectsApiService);
  /** Link writes run in order: the unlink of a wizard left empty never lands before its link. */
  private requestLinkTail: Promise<unknown> = Promise.resolve();
  /** Projects whose library pick waits for the first photo the server accepts → stop of that watch. */
  private readonly libraryPickWatches = new Map<string, () => void>();

  /** CRM request → its Albums project (null unlinks). Background: a failure keeps the project, no toast. */
  linkRequest(requestId: string, albumProjectId: string | null): void {
    this.requestLinkTail = this.requestLinkTail
      .then(() => firstValueFrom(this.projectsApi.patchSchoolRequest(requestId, { albumProjectId })))
      .catch((error: unknown) => console.warn('[Projects] request link not saved', { requestId, albumProjectId, error }));
  }

  /** `replaceUrl` — шаг настроек уже стоит на `/projects/new`: «Назад» браузера не возвращает на него второй раз. */
  enterProjectWizard(draft: CreateProjectDraft, replaceUrl = false): void {
    // Enter the wizard immediately; the real project is created in the background and its id
    // replaces `creating=1` in the query string the moment it lands (the wizard buffers uploads).
    // Query-string service/product/spreads are display-only; the durable выбор типографии lands in
    // project baseSettings via saveDraftBaseSettings.
    // Шаблон едет как ?design: мастер сеет по нему выбор, а параметр держит восстановление из проекта.
    void this.router.navigate(['/projects/new'], {
      queryParams: {
        creating: 1,
        name: draft.name,
        service: draft.service,
        product: draft.product,
        spreads: draft.spreads,
        design: draft.designId,
      },
      replaceUrl,
    });
    void this.createProjectInBackground(draft);
  }

  /** «Назад» из окна способа вернул шаг настроек: тот же проект принимает новые имя, типографию и шаблон —
   *  второй проект не создаётся. Строка запроса следует за черновиком, как при создании. */
  async updateProjectFromDraft(projectId: string, previousName: string, draft: CreateProjectDraft): Promise<void> {
    void this.router.navigate(['/projects/new'], {
      queryParams: {
        name: draft.name,
        service: draft.service,
        product: draft.product,
        spreads: draft.spreads,
        design: draft.designId,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (draft.name !== previousName) {
      await this.projects
        .renameProject(projectId, draft.name)
        .catch(() => this.toast.error('Не удалось переименовать проект — попробуйте ещё раз'));
    }

    this.rememberLibraryPick(projectId, draft.designId);
    await this.saveDraftBaseSettings(projectId, draft);
  }

  private async createProjectInBackground(draft: CreateProjectDraft): Promise<void> {
    try {
      const project = await this.projects.createProject(draft.name, draft.flowMode);
      const baseSettingsSaved = await this.saveDraftBaseSettings(project.id, draft);
      // Fire-and-forget on purpose: remembering the choice must never stand between the created
      // project and the navigation that hands it over. Awaiting a bounded profile PATCH would add
      // up to its own ceiling of dead time to every create, and widen the window in which the user
      // walks away and the URL check below drops the id of a project that was created fine.
      if (baseSettingsSaved && draft.productEntry) {
        void this.createProjectPreferences.save({
          serviceId: draft.productEntry.printServiceId,
          productId: draft.productEntry.id,
          spreads: draft.spreads,
          designId: draft.designId,
        });
      }

      if (!this.router.url.startsWith('/projects/new')) {
        // The wizard was left before the id even landed — nothing can have been uploaded into this
        // project, and the wizard's own empty-project cleanup never saw an id to delete. Same law:
        // a project with no photo on the server was never really created, so it leaves no row behind.
        // Фоновая уборка — молча: пользователь этот проект удалять не просил.
        await this.projects.deleteProject(project.id, false).catch(() => undefined);
        return;
      }

      this.rememberLibraryPick(project.id, draft.designId);

      if (draft.fromRequest) {
        // The sheet path has no wizard draft to lose: the birth mark carries the id past a reload.
        rememberBirthRequest(project.id, draft.fromRequest);
        this.linkRequest(draft.fromRequest, project.id);
      }

      // Merge the id in (and drop `creating`) — a full query replace here wipes a template/mode/step the
      // user picked before the background create landed.
      void this.router.navigate(['/projects/new'], {
        queryParams: { projectId: project.id, creating: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    } catch {
      this.toast.error('Не удалось создать проект — попробуйте ещё раз');
      if (this.router.url.startsWith('/projects/new')) void this.router.navigate(['/projects']);
    }
  }

  /** The project proved real — a photo reached the server — and its free library pick joins «Мои шаблоны».
   *  One quiet retry; a second refusal is told, never blocking: the pick still works for this project. */
  async claimLibraryPick(projectId: string): Promise<void> {
    this.stopLibraryPickWatch(projectId);
    const designId = birthLibraryPickOf(projectId);
    if (!designId) return;
    rememberBirthLibraryPick(projectId, null);
    const shop = this.injector.get(PartnerShopResource);
    try {
      await shop.requestFree(designId);
    } catch {
      try {
        await shop.requestFree(designId);
      } catch {
        this.toast.error(LIBRARY_PICK_FAILED);
      }
    }
  }

  // A free library pack picked for a project waits for the project's first photo (`claimLibraryPick`): a wizard
  // left empty, or a pick changed through «Назад», must add nothing. A pick off the library offer drops it.
  private rememberLibraryPick(projectId: string, designId: string | null): void {
    if (!designId) {
      rememberBirthLibraryPick(projectId, null);
      this.stopLibraryPickWatch(projectId);
      return;
    }

    const shop = this.injector.get(PartnerShopResource);
    const library = this.injector.get(LumirLibraryResource);
    const offered = libraryOffer(library.packs(), shop.myTemplates()).some(pack => pack.id === designId);
    rememberBirthLibraryPick(projectId, offered ? designId : null);
    if (offered) this.watchFirstPhoto(projectId);
    else this.stopLibraryPickWatch(projectId);
  }

  // The grant must not hang on the wizard being alive: ✕ pressed while the first upload is still in flight leaves the
  // upload landing in the background with no wizard to see it. The coordinator is root and outlives the page, so the
  // first file the server accepts claims the pick from here. The wizard keeps its own claim for a reload, which loses
  // this watch; whichever claim runs second finds the pick already taken and grants nothing.
  private watchFirstPhoto(projectId: string): void {
    if (this.libraryPickWatches.has(projectId)) return;
    const coordinator = this.injector.get(PhotoUploadCoordinatorService);
    const stop = coordinator.watchPhotoSync(projectId, event => {
      if (event.kind === 'upload-accepted') void this.claimLibraryPick(projectId);
    });
    this.libraryPickWatches.set(projectId, stop);
  }

  private stopLibraryPickWatch(projectId: string): void {
    this.libraryPickWatches.get(projectId)?.();
    this.libraryPickWatches.delete(projectId);
  }

  // Выбор типографии из диалога — обязателен для сборки альбома; book-assembly читает его из project baseSettings.
  private async saveDraftBaseSettings(projectId: string, draft: CreateProjectDraft): Promise<boolean> {
    const product = draft.productEntry;
    if (!product) return false;
    try {
      await this.projects.saveProjectBaseSettings(projectId, buildPhotoBookSettingsPatch(product, draft.spreads));
      return true;
    } catch {
      this.toast.error('Не удалось сохранить формат альбома — сборка пойдёт с форматом по умолчанию');
      return false;
    }
  }
}
